import { type JSX } from 'react';
import { useEffect, useState } from 'react';

import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Alert } from 'Components/Alert/Alert';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { ExceptionResponse } from 'Models/Exceptions';
import { FolderVersion } from 'Models/Folders';
import { IssueResponse } from 'Models/Issues';
import { ThirdPartyForNonTprmUser, ThirdPartyResponse, ThirdPartyService } from 'Models/TPRM';

import styles from './DeleteThirdPartyModal.module.css';

export interface DeleteThirdPartyModalProps {
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    onHideModal: () => void;
    onSuccess: () => Promise<void>;
    thirdParty: ThirdPartyResponse | ThirdPartyForNonTprmUser;
    tprmApi: ClientTPRMApi;
    userHasFullTprmAccess: boolean;
}

/** Renders a modal for confirming deletion of a Third Party. */
export const DeleteThirdPartyModal = (props: DeleteThirdPartyModalProps): JSX.Element => {
    const [failureMessage, setFailureMessage] = useState<string>();
    const [loadingThirdPartyExceptions, setLoadingThirdPartyExceptions] = useState(true);
    const [loadingThirdPartyFolders, setLoadingThirdPartyFolders] = useState(true);
    const [loadingThirdPartyIssues, setLoadingThirdPartyIssues] = useState(true);
    const [loadingThirdPartyServices, setLoadingThirdPartyServices] = useState(true);
    const [thirdPartyExceptions, setThirdPartyExceptions] = useState<ExceptionResponse[]>([]);
    const [thirdPartyFolders, setThirdPartyFolders] = useState<FolderVersion[]>([]);
    const [thirdPartyIssues, setThirdPartyIssues] = useState<IssueResponse[]>([]);
    const [thirdPartyServices, setThirdPartyServices] = useState<ThirdPartyService[]>([]);

    useEffect(() => {
        /** Get the Third Party's Exceptions. */
        const getThirdPartyExceptions = async () => {
            try {
                const response = await props.exceptionsApi.getExceptionsByOrganization(props.thirdParty.id);
                setThirdPartyExceptions(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setLoadingThirdPartyExceptions(false);
            }
        };

        /** Get the Third Party's Folders. */
        const getThirdPartyFolders = async () => {
            try {
                const response = await props.tprmApi.getAllCurrentFoldersForThirdParty(props.thirdParty.id);
                setThirdPartyFolders(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setLoadingThirdPartyFolders(false);
            }
        };

        /** Get the Third Party's Issues. */
        const getThirdPartyIssues = async () => {
            try {
                const response = await props.issuesApi.getIssuesByOrganization(props.thirdParty.id);
                setThirdPartyIssues(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setLoadingThirdPartyIssues(false);
            }
        };

        /** Get the Third Party's Services. */
        const getThirdPartyServices = async () => {
            try {
                const response = await props.tprmApi.getThirdPartyServices(props.thirdParty.id);
                setThirdPartyServices(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setLoadingThirdPartyServices(false);
            }
        };

        // Don't bother making API calls if the user doesn't have access to the data.
        if (props.userHasFullTprmAccess) {
            getThirdPartyExceptions();
            getThirdPartyFolders();
            getThirdPartyIssues();
            getThirdPartyServices();
        } else {
            setLoadingThirdPartyExceptions(false);
            setLoadingThirdPartyFolders(false);
            setLoadingThirdPartyIssues(false);
            setLoadingThirdPartyServices(false);
        }
    }, [props.exceptionsApi, props.issuesApi, props.thirdParty.id, props.tprmApi, props.userHasFullTprmAccess]);

    /**
     * Renders the HTML message for PRIVILEGED users (i.e., those WITH the admin or third-party) roles.
     * Privileged users have access to view all TPRM data, so this lists specific data that will be deleted.
     */
    const renderPrivilegedUserMessage = (): JSX.Element => {
        return (
            <ul className={styles.deleteConfirmationList}>
                <li>
                    <Text>The third party record will be deleted.</Text>
                </li>
                {thirdPartyServices.length > 0 && (
                    <li>
                        <Text>All of the third party's services will be deleted:</Text>
                        <ul>
                            {thirdPartyServices.map((thirdPartyService) => (
                                <li key={thirdPartyService.id}>
                                    <Text>{thirdPartyService.name}</Text>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
                {thirdPartyFolders.length > 0 && (
                    <li>
                        <Text>All of the third party's folders and documents will be deleted:</Text>
                        <ul>
                            {thirdPartyFolders.map((thirdPartyFolder) => (
                                <li key={thirdPartyFolder.id}>
                                    <Text>{thirdPartyFolder.name}</Text>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
                {thirdPartyIssues.length > 0 && (
                    <li>
                        <Text>All issues mapped to the third party will be deleted:</Text>
                        <ul>
                            {thirdPartyIssues.map((thirdPartyIssue) => (
                                <li key={thirdPartyIssue.id}>
                                    <Text>{thirdPartyIssue.title}</Text>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
                {thirdPartyExceptions.length > 0 && (
                    <li>
                        <Text>All exceptions mapped to the third party will be deleted:</Text>
                        <ul>
                            {thirdPartyExceptions.map((thirdPartyException) => (
                                <li key={thirdPartyException.id}>
                                    <Text>{thirdPartyException.title}</Text>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
            </ul>
        );
    };

    /**
     * Renders the HTML message for UNPRIVILEGED users (i.e., those WITHOUT the admin or third-party) roles.
     * Unprivileged users do not have access to view all TPRM data, so this shows a more generic message.
     * Note: While unprivileged users cannot view all TPRM data, they are still allowed to delete third parties that are in "draft" status. It's unlikely, but draft third parties can potentially have issues, exceptions, and folders that will be deleted along with the third party.
     */
    const renderUnprivilegedUserMessage = (): JSX.Element => {
        return (
            <ul className={styles.deleteConfirmationList}>
                <li>
                    <Text>The third party record will be deleted.</Text>
                </li>
                <li>
                    <Text>All of the third party's services will be deleted.</Text>
                </li>
                <li>
                    <Text>All of the third party's folders and documents will be deleted.</Text>
                </li>
                <li>
                    <Text>All issues mapped to the third party will be deleted.</Text>
                </li>
                <li>
                    <Text>All exceptions mapped to the third party will be deleted.</Text>
                </li>
            </ul>
        );
    };

    if (loadingThirdPartyExceptions || loadingThirdPartyFolders || loadingThirdPartyIssues || loadingThirdPartyServices) {
        return <ConfirmationModal isLoading headerText="Delete Third Party" hideModal={props.onHideModal} failureMessage={failureMessage} />;
    } else {
        return (
            <ConfirmationModal
                operationType={ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM}
                headerText="Delete Third Party"
                areYouSureText={`Are you sure you want to delete "${props.thirdParty.name}"?`}
                performOperation={async () => {
                    await props.tprmApi.deleteThirdParty(props.thirdParty.id);
                    await props.onSuccess();
                    return 'Third party deleted.';
                }}
                hideModal={props.onHideModal}
            >
                {failureMessage && <Alert type="error">{failureMessage}</Alert>}
                {props.userHasFullTprmAccess && renderPrivilegedUserMessage()}
                {!props.userHasFullTprmAccess && renderUnprivilegedUserMessage()}
            </ConfirmationModal>
        );
    }
};
